<template>
  <v-container fluid mb-4>
    <v-row align-content="center" justify="center">
      <v-col align-self="center">
        <v-card class="elevation-12">
          <template v-for="(item,index) in newOrders">
            <new-order-alert
                :key="index"
                :lot-type="lot.lotType"
                :order="item.order"
                :index="index"
                :timeout="item.timestamp &&
                  item.timestamp.diff($moment(), TIME_UNITS.MILLISECONDS) > 1
                  ? item.timestamp.diff($moment(), TIME_UNITS.MILLISECONDS)
                  : 0"
                @close="closeAlert" />
          </template>
          <v-toolbar :dark="$vuetify.theme.dark" color="primary">
            <v-toolbar-title>
              {{ $t('Процесс торгов (подача ценового предложения по торговой процедуре № {0})', [ lot.lotNumber ] ) }}
            </v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-progress-linear v-if="isLoading" color="loader" indeterminate />
            <v-expansion-panels>
              <v-expansion-panel v-test="{ panel: '' }">
                <v-expansion-panel-header>
                  <div>
                    <v-icon>
                      gavel
                    </v-icon>
                    {{ $t('Информация о текущей сессии') }}
                    ({{ $t(currentSession.sessionType
                      ? SESSION_TYPE_ENUM[currentSession.sessionType]
                      : '') }})
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row>
                    <v-col>
                      <text-view
                          :display="lot.lotDescription"
                          :label="$t('Описание торговой процедуры')" />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-data-table
                          :headers="headers1"
                          :items="sessionInfo"
                          class="elevation-1"
                          :items-per-page="1"
                          item-key="sessionId"
                          disable-sort-by
                          dense
                          single-expand
                          show-expand
                          hide-default-footer
                          :locale="systemLocale">
                        <!-- eslint-disable-next-line vue/valid-v-slot -->
                        <template v-slot:item.sessionNumber="{ item }">
                          {{ item.sessionNumber }}
                        </template>
                        <!-- eslint-disable-next-line vue/valid-v-slot -->
                        <template v-slot:item.dateStart="{ item }">
                          {{ item.dateStart
                            ? $moment(item.dateStart).format('DD.MM.YYYY HH:mm')
                            : null }}
                        </template>
                        <!-- eslint-disable-next-line vue/valid-v-slot -->
                        <template v-slot:item.dateEndWithProlongation="{ item }">
                          {{ item.dateEndWithProlongation
                            ? $moment(item.dateEndWithProlongation).format('DD.MM.YYYY HH:mm:ss')
                            : null }}
                        </template>

                        <template v-slot:expanded-item="{ headers, item }">
                          <td :colspan="headers.length">
                            <v-row
                                v-if="!!item.tradeDirectionType"
                                class="pt-4">
                              <text-view
                                  :display="$t(TRADE_DIRECTION_TYPE_ENUM[item.tradeDirectionType])"
                                  :label="$t('Направление торгов')" />
                            </v-row>
                            <v-row class="pb-6 px-0">
                              <v-col class="px-2">
                                <positions-list
                                    v-if="item.sessionId"
                                    :lot="lot"
                                    :positions="positions"
                                    dense
                                    hide-header />
                              </v-col>
                            </v-row>
                          </td>
                        </template>
                      </v-data-table>
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
            <v-row class="my-3">
              <v-col>
                <v-data-table
                    :headers="headers2"
                    :items="sessionTimeInfo"
                    class="elevation-1"
                    :items-per-page="1"
                    item-key="finished"
                    disable-sort-by
                    dense
                    hide-default-footer
                    :locale="systemLocale">
                  <!-- eslint-disable-next-line  vue/valid-v-slot -->
                  <template v-slot:item.currentTime="{ item }">
                    {{ item.currentTime }}
                  </template>
                  <!-- eslint-disable-next-line vue/valid-v-slot -->
                  <template v-slot:item.timeLeft>
                    <timer
                        :now="serverTime"
                        :start-time="currentSession.dateStart"
                        :end-time="currentSession.dateEndWithProlongation"
                        @finished="onTimeFinished" />
                  </template>
                  <!-- eslint-disable-next-line  vue/valid-v-slot -->
                  <template v-slot:item.duration="{ item }">
                    {{ item.duration }}
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <general-error v-if="!!generalError" />
            <v-row
                v-if="canAddOrders && !stopSessionAlert && lot.lotNumber"
                justify="start"
                class="text-right elevation-2 mx-2 my-2">
              <v-col class="text-right">
                <add-order-dialog
                    v-if="addOrderDialog.show"
                    is-fix-premium
                    :dialog-prop="addOrderDialog"
                    @close="closeDialog('addOrderDialog')" />
                <add-divisible-volume-order-dialog
                    v-if="addDivisibleVolumeOrderDialog.show"
                    is-fix-premium
                    :dialog-prop="addDivisibleVolumeOrderDialog"
                    @close="closeDialog('addDivisibleVolumeOrderDialog')" />
                <v-btn
                    id="addOrderBtn"
                    color="primary"
                    @click="lot.lotType === LOT_TYPES.REQUEST_OFFERS_DIVISIBLE_VOLUME
                      ? openAddDivisibleVolumeOrderDialog()
                      : openAddOrderDialog()">
                  {{ $t('Создать ценовое предложение...') }}
                </v-btn>
              </v-col>
            </v-row>
            <v-row v-if="!!stopSessionAlert">
              <v-col my-3>
                <v-alert
                    class="text-center"
                    outlined
                    :value="!!stopSessionAlert"
                    type="error">
                  {{ stopSessionAlert }}
                </v-alert>
              </v-col>
            </v-row>
            <v-card-title>
              <v-row>
                <v-col class="text-right">
                  <icon-tip
                      icon="swap_vertical_circle"
                      :icon-color="connected
                        ? 'success'
                        : 'error'"
                      :tip="$t(connected
                        ? 'Подключен'
                        : 'Отключен')" />
                  <icon-tip
                      v-if="tradeDirectionIcon"
                      :icon="tradeDirectionIcon"
                      icon-color="primary"
                      :tip="$t(TRADE_DIRECTION_TYPE_ENUM[currentSession.tradeDirectionType])" />
                </v-col>
              </v-row>
            </v-card-title>
            <v-row>
              <orders-list
                  v-if="orders.length"
                  :orders="orders"
                  :lot="lot"
                  :show-additional-info="currentSession.enableAdditionalOfferParams"
                  :session="currentSession" />
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-row class="text-left">
              <v-col ma-1>
                <v-btn
                    id="toLotBtn"
                    v-test="{ btn: 'toLot' }"
                    class="ma-2"
                    color="primary"
                    @click.stop="toLot">
                  {{ $t('К торговой процедуре') }}
                </v-btn>
                <v-btn
                    id="toLotListBtn"
                    v-test="{ btn: 'toLotList' }"
                    class="ma-2"
                    color="primary"
                    @click.stop="toLotList">
                  {{ $t('К списку торговых процедур') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import AddOrderDialog from './AddOrderDialog.vue'
  import AddDivisibleVolumeOrderDialog from './AddDivisibleVolumeOrderDialog.vue'
  import OrdersList from '@/components/reused/orders/OrdersList.vue'
  import PositionsList from '@/components/reused/positions/PositionsList.vue'
  import Timer from '@/components/reused/ui/Timer.vue'
  import IconTip from '@/components/reused/ui/IconTip.vue'
  import TextView from '@/components/reused/ui/TextView.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import NewOrderAlert from '@/components/reused/orders/NewOrderAlert.vue'
  import lotMixin from '@/utils/mixins/scoped/lotMixin'
  import signalRMixin from '@/utils/mixins/scoped/signalRMixin.js'
  import timerMixin from '@/utils/mixins/scoped/timerMixin.js'
  import { mapActions, mapState, mapGetters } from 'vuex'

  import {
    TRADE_DIRECTION_TYPE_ENUM, SESSION_TYPE_ENUM,
    SESSION_LOCK_STATUS_ENUM
  } from '@/constants/enums'
  import {
    LOT_TYPES,
    TRADE_DIRECTION_TYPES,
    SESSION_LOCK_STATUSES,
    TIME_UNITS,
    PREMIUM_TYPES,
    SESSION_TYPES,
    LOT_ACTIONS,
    PRIVACY_TYPES
  } from '@/constants/business'
  // import { ERROR_CODE_BIDDING } from '@/constants/settings'
  import {
    // URL_TWO_STAGED_OFFERS_HUB,
    // URL_AUCTION_HUB,
    URL_REQUEST_OFFERS_HUB,
    URL_REQUEST_OFFERS_DIVISIBLE_VOLUME_HUB,
    SIGNALR_MIXIN_PARAMS,
    SIGNALR_EVENT_NAMES,
    SIGNALR_INVOKE_NAMES,
    SIGNALR_CALLBACK_NAMES
  } from '@/constants/signalR'
  import {
    LOT_MANAGEMENT, LOT_SESSIONS_MANAGEMENT,
    LOT_ORDERS, SYSTEM_SETTINGS, CURRENT_USER,
    LOT_POSITIONS_MANAGEMENT, LOTS

  } from '@/constants/store.js'

  export default {
    components: {
      OrdersList,
      Timer,
      IconTip,
      TextView,
      GeneralError,
      NewOrderAlert,
      AddOrderDialog,
      AddDivisibleVolumeOrderDialog,
      PositionsList
    },
    mixins: [lotMixin,
             timerMixin,
             signalRMixin({
               [SIGNALR_MIXIN_PARAMS.GET_HUB_METHOD_NAME]: SIGNALR_CALLBACK_NAMES.GET_HUB_NAME,
               [SIGNALR_MIXIN_PARAMS.EVENTS_LIST]: [
                 {
                   [SIGNALR_MIXIN_PARAMS.EVENT_NAME]: SIGNALR_EVENT_NAMES.ADD_NEW_ORDER,
                   [SIGNALR_MIXIN_PARAMS.CALLBACK_NAME]: SIGNALR_CALLBACK_NAMES.ADD_NEW_ORDER
                 },
                 {
                   [SIGNALR_MIXIN_PARAMS.EVENT_NAME]: SIGNALR_EVENT_NAMES.UPDATE_ORDERS,
                   [SIGNALR_MIXIN_PARAMS.CALLBACK_NAME]: SIGNALR_CALLBACK_NAMES.UPDATE_ORDERS
                 },
                 {
                   [SIGNALR_MIXIN_PARAMS.EVENT_NAME]: SIGNALR_EVENT_NAMES.SESSION_LOCK_STATUS_CHANGED,
                   [SIGNALR_MIXIN_PARAMS.CALLBACK_NAME]: SIGNALR_CALLBACK_NAMES.SESSION_LOCK_STATUS_CHANGED
                 }
               ],
               [SIGNALR_MIXIN_PARAMS.INVOKE_LIST]: [{
                 [SIGNALR_MIXIN_PARAMS.METHOD_NAME]: SIGNALR_INVOKE_NAMES.ADD_TO_GROUP,
                 [SIGNALR_MIXIN_PARAMS.GET_GROUP_NAME_METHOD]: SIGNALR_CALLBACK_NAMES.GET_SIGNALR_GROUP_NAME
               }],
               [SIGNALR_MIXIN_PARAMS.RECONNECT_CALLBACK_NAME]: SIGNALR_CALLBACK_NAMES.UPDATE_ORDERS
             })],
    props: {
      lotId: {
        type: String,
        required: true
      },
      lotType: {
        type: String,
        required: true
      }
    },
    data () {
      return {
        isLoading: false,
        TRADE_DIRECTION_TYPE_ENUM,
        SESSION_TYPE_ENUM,
        TIME_UNITS,
        LOT_TYPES,
        sessionTime: {
          currentTime: null,
          timeLeft: null,
          duration: null,
          finished: false,
          start: false
        },
        sessionLockStatus: null,
        addOrderDialog: {
          isFixPremium: false,
          showAdditionalInfo: false,
          showZero: false,
          currency: null,
          positions: null,
          callback: null,
          show: false
        },
        addDivisibleVolumeOrderDialog: {
          params: null,
          positions: null,
          callback: null,
          show: false
        },
        newOrders: []
      }
    },
    computed: {
      ...mapState(LOT_MANAGEMENT.NAMESPACE, {
        currentLot: state => (state[LOT_MANAGEMENT.STATE.CURRENT_LOT])
      }),
      ...mapState(CURRENT_USER.NAMESPACE, {
        hash: state => (state[CURRENT_USER.STATE.HASH] || null)
      }),
      ...mapState(LOT_ORDERS.NAMESPACE, {
        ordersInfo: state => (state[LOT_ORDERS.STATE.ORDERS] || {})
      }),
      ...mapState(LOT_POSITIONS_MANAGEMENT.NAMESPACE, {
        positions: state => (state[LOT_POSITIONS_MANAGEMENT.STATE.POSITIONS] || [])
      }),
      ...mapState(LOT_SESSIONS_MANAGEMENT.NAMESPACE, {
        currentSessionData: state => (state[LOT_SESSIONS_MANAGEMENT.STATE.CURRENT_SESSION] || {})
      }),
      ...mapGetters(LOTS.NAMESPACE, {
        actionsByLotId: LOTS.GETTERS.ACTIONS_BY_LOT_ID
      }),
      lot () {
        return this.currentLot || {
          lotId: this.lotId,
          lotType: this.lotType
        }
      },
      isFixPremium () {
        return this.lot.premiumType === PREMIUM_TYPES.FIX
      },
      isRasing () {
        return this.currentSession.tradeDirectionType === TRADE_DIRECTION_TYPES.RAISING
      },
      tradeDirectionIcon () {
        if (!this.currentSession.tradeDirectionType) {
          return null
        }
        return this.isRasing
          ? 'trending_up'
          : 'trending_down'
      },
      orders () {
        return this.ordersInfo.items || []
      },
      sessionInfo () {
        if (this.currentSession.sessionId &&
          this.sessionTime.currentTime) {
          return [
            { ...this.currentSession }
          ]
        }
        return []
      },
      sessionTimeInfo () {
        return [this.sessionTime]
      },
      actions () {
        const lotActions = this.actionsByLotId(this.lotId) || {}
        return lotActions.actions || []
      },
      canAddOrders () {
        return this.sessionInProcess && this.actions.includes(LOT_ACTIONS.ADD_ORDERS)
      },
      // все данные по текущей сессии
      currentSession () {
        const lastSession = this.lastOrCurrentSession
        if (!lastSession) {
          return {}
        }
        const session = this.currentSessionData
        return {
          ...session,
          ...lastSession,
          lockStatus: this.sessionLockStatus || session.lockStatus,
          dateEndWithProlongation: this.ordersInfo.endSessionTimeExtension ||
            session.dateEndWithProlongation ||
            lastSession.dateEndWithProlongation
        }
      },
      sessionInProcess () {
        const session = this.currentSession
        if (!session ||
          !session.dateStart ||
          !session.dateEndWithProlongation) {
          return false
        }
        if (session.lockStatus &&
          session.lockStatus !== SESSION_LOCK_STATUSES.UNLOCKED) {
          return false
        }
        const now = this.sessionTime?.currentTime
        ? this.$moment(this.sessionTime.currentTime, 'DD.MM.YYYY HH:mm:ss')
        : this.$moment()
        const dateStart = this.$moment(session.dateStart)
        const dateEnd = this.$moment(session.dateEndWithProlongation)
        return now.isBetween(dateStart, dateEnd)
      },
      headers1 () {
        return [
          { text: this.$t('№ сессии'), align: 'center', sortable: false, value: 'sessionNumber' },
          { text: this.$t('Дата начала торгов'), align: 'center', sortable: false, value: 'dateStart' },
          { text: this.$t('Время завершения торговой сессии'), align: 'center', sortable: false, value: 'dateEndWithProlongation' },
          { text: '', value: 'data-table-expand' }
        ]
      },
      headers2 () {
        return [
          { text: this.$t('Текущее время'), align: 'center', sortable: false, value: 'currentTime' },
          { text: this.$t(this.sessionInProcess || this.sessionTime.finished ? 'Время до конца торгов' : 'Время до начала торгов'), align: 'center', sortable: false, value: 'timeLeft' },
          { text: this.$t('Продолжительность торгов'), align: 'center', sortable: false, value: 'duration' }
        ]
      },
      stopSessionAlert () {
        if (this.currentSession.lockStatus !== SESSION_LOCK_STATUSES.UNLOCKED && !this.currentSession.lockStatus) {
          return this.$t(SESSION_LOCK_STATUS_ENUM[this.currentSession.lockStatus])
        }
        return null
      },
      showZero () {
        return !!this.orders.length &&
          (this.currentSession.sessionType === SESSION_TYPES.PUBLIC_OFFERS /* ||
            this.currentSession.sessionType === SESSION_TYPES.PUBLIC_TWO_STAGED_OFFERS */)
      }
    },
    watch: {
      'ordersInfo.sessionLockStatus' (newVal) {
        this.setLockStatus(newVal)
      },
      'ordersInfo.nextPremiums' (newVal) {
        this.setNextPremiums(newVal)
      },
      'currentSession.sessionId' (newVal, oldVal) {
        if (!!newVal && !!oldVal && newVal !== oldVal) {
          this.initialize()
        }
      },
      sessionInProcess (newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this[SIGNALR_CALLBACK_NAMES.UPDATE_ORDERS]()
          const model = {
            lotId: this.lotId,
            lotType: this.lotType
          }
          this.startSignalR()
          this.getLotActions()
          this.getPositionsListAction(model)
        }
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        if (vm.lotId) {
          vm.initialize()
        }
      })
    },
    // чистим установщик текущего времени и подключение
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(LOTS.NAMESPACE, {
        getLotActionsAction: LOTS.ACTIONS.GET_LOT_ACTIONS,
        clearLotActionsAction: LOTS.ACTIONS.CLEAR_LOT_ACTIONS
      }),
      ...mapActions(LOT_MANAGEMENT.NAMESPACE, {
        getLotInfoAction: LOT_MANAGEMENT.ACTIONS.GET_LOT_INFO,
        clearLotInfoAction: LOT_MANAGEMENT.ACTIONS.CLEAR_LOT_INFO
      }),
      ...mapActions(LOT_SESSIONS_MANAGEMENT.NAMESPACE, {
        getSessionsListAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.GET_SESSIONS_LIST,
        getSessionInfoAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.GET_SESSION_INFO,
        clearSessionInfoAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.CLEAR_SESSION_INFO,
        clearSessionsListAction: LOT_SESSIONS_MANAGEMENT.ACTIONS.CLEAR_SESSIONS_LIST
      }),
      ...mapActions(LOT_POSITIONS_MANAGEMENT.NAMESPACE, {
        getPositionsListAction: LOT_POSITIONS_MANAGEMENT.ACTIONS.GET_POSITIONS_LIST,
        clearPositionsListAction: LOT_POSITIONS_MANAGEMENT.ACTIONS.CLEAR_POSITIONS_LIST
      }),
      ...mapActions(LOT_ORDERS.NAMESPACE, {
        getOrdersListAction: LOT_ORDERS.ACTIONS.GET_ORDERS_LIST,
        addOrderAction: LOT_ORDERS.ACTIONS.ADD_ORDER,
        clearOrdersListAction: LOT_ORDERS.ACTIONS.CLEAR_ORDERS_LIST
      }),
      ...mapActions(SYSTEM_SETTINGS.NAMESPACE, {
        getServerTimeAction: SYSTEM_SETTINGS.ACTIONS.GET_SERVER_TIME,
        clearServerTimeAction: SYSTEM_SETTINGS.ACTIONS.CLEAR_SERVER_TIME
      }),
      closeAlert: function (index) {
        this.newOrders.splice(index, 1)
      },
      clearStore: async function () {
        await Promise.all(
          [
            this.clearServerTimeAction(),
            this.clearLotInfoAction(),
            this.clearSessionInfoAction(),
            this.clearSessionsListAction(),
            this.clearOrdersListAction(),
            this.clearPositionsListAction(),
            this.clearLotActionsAction()
          ])
      },
      initialize: async function () {
        try {
          const model = {
            lotId: this.lotId,
            lotType: this.lotType
          }
          this.isLoading = true

          await Promise.all([
            this.getLotInfoAction(model),
            this.getSessionsListAction(model),
            this.getPositionsListAction(model),
            this.getServerTime(),
            this.getLotActions()
          ])

          if (this.currentSession.sessionId) {
            await this.getSessionInfo()
            await this.getOrdersList()
            this.startSignalR()
          }
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      getOrdersList: async function () {
        const model = {
          lotId: this.lotId,
          lotType: this.lotType,
          sessionId: this.currentSession.sessionId
        }
        await this.getOrdersListAction(model)
      },
      getSessionInfo: async function () {
        const model = {
          lotId: this.lotId,
          lotType: this.lotType,
          sessionId: this.currentSession.sessionId,
          sessionType: this.currentSession.sessionType
        }
        await this.getSessionInfoAction(model)
        this.setLockStatus(this.currentSession.lockStatus)
      },
      getLotActions: async function () {
        this.isLoading = true
        try {
          await this.getLotActionsAction({
            privacyType: PRIVACY_TYPES.PERSONAL,
            lotId: this.lotId,
            lotType: this.lotType
          })
          this.isLoading = false

          this.loaded = true
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      setLockStatus: function (status) {
        if (this.sessionLockStatus !== status) {
          this.sessionLockStatus = status
        }
      },
      [SIGNALR_CALLBACK_NAMES.GET_SIGNALR_GROUP_NAME]: function () {
        return this.currentSession.sessionId
      },
      [SIGNALR_CALLBACK_NAMES.GET_HUB_NAME]: function () {
        switch (this.lot.lotType) {
        case LOT_TYPES.REQUEST_OFFERS:
          return URL_REQUEST_OFFERS_HUB
        case LOT_TYPES.REQUEST_OFFERS_DIVISIBLE_VOLUME:
          return URL_REQUEST_OFFERS_DIVISIBLE_VOLUME_HUB
        }
      },
      [SIGNALR_CALLBACK_NAMES.SESSION_LOCK_STATUS_CHANGED]: function (sessionId, sessionLockStatus) {
        this.setLockStatus(sessionLockStatus)
      },
      // обработчик добавления нового ценового предложения (при получении сообщения от хаба)
      [SIGNALR_CALLBACK_NAMES.ADD_NEW_ORDER]: function (order) {
        // вывести оповещение о новой заявке
        this.newOrders.push({
          order,
          timestamp: this.$moment().add(10000, TIME_UNITS.MILLISECONDS)
        })
        // todo:
        //  const isOwnOrder = (hash === this.hash)

        // if (isOwnOrder &&
        //   this.generalError &&
        //   this.generalError.description === ERROR_CODE_BIDDING) {
        //   this.hideErrorAction()
        // }
      },
      // обновление данных текущей сессии по сообщению от хаба signalR
      [SIGNALR_CALLBACK_NAMES.UPDATE_ORDERS]: async function () {
        try {
          this.isLoading = true
          await this.getOrdersList()
          await this.getServerTime()
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      setNextPremiums: function (nextPremiums) {
        if (!nextPremiums || !nextPremiums.length) {
          this.addOrderDialog.positions = this.positions
          return
        }
        const positionsWithNextPremiums = this.positions.map(p => {
          const nextPremiumInfo = nextPremiums.find(np => np.positionId === p.positionId) || {}
          return {
            ...p,
            ...nextPremiumInfo
          }
        })

        this.addOrderDialog.showZero = this.showZero
        this.addOrderDialog.positions = positionsWithNextPremiums
      },
      addOrder: async function (order) {
        try {
          this.isLoading = true
          await this.addOrderAction({
            lotId: this.lotId,
            lotType: this.lotType,
            sessionId: this.currentSession.sessionId,
            order
          })
          this.isLoading = false
        } finally {
          this.isLoading = false
        }
      },
      // получить серверное время и установить его периодическое обновление
      getServerTime: async function () {
        await this.getServerTimeAction()

        if (this.timer) {
          this.timer.stop()
          // передаем время начала отсчета (серверное, если получили)
          this.timer.startInterval(
            // интервал в мсек
            '1000',
            // метод, для вызова по таймеру
            this.setCurrentTime)
        }
      },
      // установка флага о завершении сессии, сделан для динамического отслеживания
      // и отображения/скрытия возможности отправки ценовых предложений
      onTimeFinished: async function () {
        this.sessionTime.finished = true

        if (this.currentSession) {
          const start = this.$moment(this.currentSession.dateStart)
          const diff = this.$moment(this.currentSession.dateEndWithProlongation).diff(start) // в переменной diff будет значение в милисекундах
          this.sessionTime.duration = this.getHumanizedDurationString(diff)
        }
        // await this.initialize()
      },
      // установка текущего времени и длительности сессии
      setCurrentTime: function (milliseconds) {
        const now = this.serverTime.add(milliseconds, TIME_UNITS.MILLISECONDS)
        this.sessionTime.currentTime = now.format('DD.MM.YYYY HH:mm:ss')
        if (this.sessionInProcess) {
          const diff = now.diff(this.currentSession.dateStart) // в переменной diff будет значение в милисекундах
          this.sessionTime.duration = this.getHumanizedDurationString(diff)
        }
      },
      openAddOrderDialog: function () {
        // this.addOrderDialog.positions = this.positions
        this.setNextPremiums(this.ordersInfo.nextPremiums)
        this.addOrderDialog.currency = this.lot.currency
        this.addOrderDialog.showZero = this.showZero
        this.addOrderDialog.isFixPremium = this.isFixPremium
        this.addOrderDialog.showAdditionalInfo = this.currentSession.enableAdditionalOfferParams
        this.addOrderDialog.callback = this.addOrder
        this.addOrderDialog.show = true
      },
      openAddDivisibleVolumeOrderDialog: function () {
        this.addDivisibleVolumeOrderDialog.params = {
          maxPartsCount: this.currentSession.maxPartsCount,
          onePartVolume: this.currentSession.onePartVolume,
          priceStart: this.currentSession.startPremiums[0]?.premium,
          nextPrice: this.ordersInfo?.nextPremiums[0]?.premium || 1,
          unit: this.positions[0]?.unit || '',
          sessionId: this.currentSession.sessionId,
          sessionType: this.currentSession.sessionType,
          lotId: this.lot.lotId,
          lotType: this.lot.lotType
        }
        this.addDivisibleVolumeOrderDialog.callback = this.getServerTime
        this.addDivisibleVolumeOrderDialog.show = true
      },
      toLot: function () {
        this.goToRoute(`${this.urlRootPath}/lot/${this.lotType}/${this.lotId}`)
      },
      toLotList: function () {
        this.goToRoute(`${this.urlRootPath}/lots/personal/registered`)
      }
    }
  }
</script>
